import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import OnboardingPageHeader from '../ComponentOnboarding/OnboardingPageHeader';
import { OnboardingMultiEmailInput } from '../ComponentOnboarding/OnboardingMultiEmailInput';
import {
  getGithubBranches,
  getGithubEnvironments,
  getGithubWorkflowJobs,
  getGithubWorkflows,
} from '@/components/smartai/api/githubOnboarding';
import { getRepositoryDetails } from '@/components/smartai/api/getRepositoryDetails';
import { getBranchesDetails } from '@/components/smartai/api/getBranchDetails';
import { getPipelineDetails } from '@/components/smartai/api/getPipelineDetails';
import { getPipelineStages } from '@/components/smartai/api/getPipelineStages';
import { getReleasePipelines } from '@/components/smartai/api/getReleasePipelines';
import { validateCaptureUrl } from '@/components/smartai/api/captureApi';
import {
  type OnboardedData,
  useComponentList,
} from '@/components/smartai/api/getComponentList';
import { fieldErrorClass } from '../ComponentOnboarding/onboarding-layout';
import type { ScmProviderId } from '@/types/scm-provider';
import type {
  AddRepositoryPickSelections,
  AdoDetectRepository,
  ResourceOption,
} from './addRepositoryTypes';

export type { ResourceOption } from './addRepositoryTypes';

const ENVIRONMENT_ALREADY_ONBOARDED_MESSAGE =
  'This environment is already onboarded. Please select a different environment.';

const normalizeRepoUrl = (url: string) =>
  url
    .trim()
    .replace(/\.git$/i, '')
    .replace(/\/+$/, '')
    .toLowerCase();

function getOnboardedEnvironmentsForRepo(
  components: OnboardedData[],
  organization: string,
  repoName: string,
  repoRemoteUrl: string,
  provider: ScmProviderId,
): string[] {
  if (!repoName.trim()) return [];

  const normalizedUrl = normalizeRepoUrl(repoRemoteUrl);
  const envs = new Set<string>();

  for (const component of components) {
    if (component.isDeleted) continue;
    if (component.provider && component.provider !== provider) continue;

    const orgMatches =
      !component.orgName ||
      !organization ||
      component.orgName.toLowerCase() === organization.toLowerCase();
    if (!orgMatches) continue;

    const matchesRepo =
      component.repoName === repoName ||
      normalizeRepoUrl(component.repoUrl) === normalizedUrl;
    if (!matchesRepo) continue;

    const env = component.environment?.trim();
    if (env) envs.add(env);
  }

  return Array.from(envs);
}

function useInlineListWheelScroll() {
  const ref = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const onWheel = (event: WheelEvent) => {
      event.stopPropagation();
      if (node.scrollHeight <= node.clientHeight) return;
      node.scrollTop += event.deltaY;
      event.preventDefault();
    };

    node.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => node.removeEventListener('wheel', onWheel, { capture: true });
  }, []);

  return ref;
}

type FilterSelectProps = {
  id: string;
  label: string;
  items: ResourceOption[];
  className?: string;
  disabled?: boolean;
  loading?: boolean;
} & (
  | {
      multiple?: false;
      value: string;
      onSelect: (value: string) => void;
    }
  | {
      multiple: true;
      value: string[];
      onSelect: (value: string[]) => void;
    }
);

export const FilterSelect = ({
  id,
  label,
  items,
  value,
  onSelect,
  className,
  disabled = false,
  loading = false,
  multiple = false,
}: FilterSelectProps) => {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(true);
  const listRef = useInlineListWheelScroll();
  const selectedValues: string[] = multiple
    ? Array.isArray(value)
      ? value
      : []
    : typeof value === 'string' && value
      ? [value]
      : [];

  const hasSelection = multiple ? selectedValues.length > 0 : Boolean(value);
  const isCollapsed = hasSelection && !expanded;

  const selectedLabel = useMemo(() => {
    if (multiple) {
      return selectedValues
        .map(itemValue => items.find(item => item.value === itemValue)?.label ?? itemValue)
        .join(', ');
    }
    return items.find(item => item.value === value)?.label ?? String(value ?? '');
  }, [items, multiple, selectedValues, value]);

  useEffect(() => {
    if (!hasSelection) {
      setExpanded(true);
      setQuery('');
    }
  }, [hasSelection]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      item =>
        item.label.toLowerCase().includes(q) ||
        item.value.toLowerCase().includes(q),
    );
  }, [items, query]);

  const handleSelect = (itemValue: string) => {
    if (disabled || loading) return;
    if (multiple) {
      const next = selectedValues.includes(itemValue)
        ? selectedValues.filter(current => current !== itemValue)
        : [...selectedValues, itemValue];
      (onSelect as (value: string[]) => void)(next);
      setQuery('');
      setExpanded(false);
      return;
    }
    (onSelect as (value: string) => void)(itemValue);
    setQuery('');
    setExpanded(false);
  };

  return (
    <div className={cn('mt-6', className)}>
      <label
        htmlFor={id}
        className="mb-2 flex items-center gap-2 text-[0.9375rem] font-semibold text-[var(--inv-text)]">
        {label}
        {loading ? <Spinner compact /> : null}
      </label>
      <div className="relative mb-2">
        {!isCollapsed ? (
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--inv-text-muted)]" />
        ) : null}
        <input
          id={id}
          type="text"
          value={isCollapsed ? selectedLabel : query}
          placeholder={isCollapsed ? undefined : 'Filter by name'}
          readOnly={isCollapsed}
          disabled={disabled || loading}
          onClick={() => {
            if (isCollapsed) setExpanded(true);
          }}
          onChange={event => {
            if (!isCollapsed) setQuery(event.target.value);
          }}
          className={cn(
            'box-border h-10 w-full rounded-[var(--inv-radius-control,12px)] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] py-0 pr-3 font-mono text-sm font-normal text-[var(--inv-text)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))] outline-none placeholder:font-mono placeholder:text-[var(--inv-text-muted)] focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)]',
            isCollapsed ? 'cursor-pointer pl-3' : 'pl-9',
            (disabled || loading) && 'cursor-not-allowed opacity-50',
          )}
        />
      </div>
      {!isCollapsed && items.length > 0 ? (
        <div
          className={cn(
            'overflow-hidden rounded-[var(--inv-radius-control,12px)] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]',
            (disabled || loading) && 'opacity-50',
          )}>
          <ul
            ref={listRef}
            className="m-0 max-h-56 list-none overflow-y-auto overscroll-contain p-0 font-mono [scrollbar-color:var(--inv-border-strong,#cfc4b6)_transparent] [scrollbar-width:thin]"
            role="listbox"
            aria-multiselectable={multiple || undefined}>
            {filtered.map(item => {
              const isSelected = selectedValues.includes(item.value);
              return (
                <li key={item.value} className="list-none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={disabled || loading}
                    className={cn(
                      'flex w-full items-center gap-2.5 border-0 border-b border-solid border-[var(--inv-border)] px-3 py-2 text-left font-mono text-sm font-normal transition-colors last:border-b-0',
                      isSelected
                        ? 'bg-[var(--inv-accent-soft)] text-[var(--inv-text)]'
                        : 'bg-transparent text-[var(--inv-text)] hover:bg-[var(--inv-bg,#faf8f5)]',
                      (disabled || loading) && 'cursor-not-allowed',
                    )}
                    onClick={() => handleSelect(item.value)}>
                    {multiple ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-solid',
                          isSelected
                            ? 'border-[var(--inv-accent)] bg-[var(--inv-accent)] text-[var(--inv-on-accent)]'
                            : 'border-[var(--inv-border-strong,#d4c4b0)] bg-white',
                        )}>
                        {isSelected ? (
                          <Check className="h-2 w-2" strokeWidth={3} />
                        ) : null}
                      </span>
                    ) : (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-solid',
                          isSelected
                            ? 'border-[var(--inv-accent)] bg-[var(--inv-accent)]'
                            : 'border-[var(--inv-border-strong,#d4c4b0)] bg-white',
                        )}>
                        {isSelected ? (
                          <span className="h-1.5 w-1.5 rounded-full bg-[var(--inv-on-accent,#fff)]" />
                        ) : null}
                      </span>
                    )}
                    <span className="truncate font-mono font-normal">
                      {item.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export function AddRepositoryBackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      aria-label="Back"
      onClick={onBack}
      className={cn(
        'mb-4 flex h-8 w-8 items-center justify-center rounded-full',
        'border-0 bg-transparent p-0 shadow-none outline-none',
        'text-[var(--inv-accent)]',
        'transition-[color,background-color] duration-150',
        'hover:bg-[var(--inv-surface-muted)] hover:text-[var(--inv-accent-hover,var(--inv-accent))]',
        'focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--inv-bg)]',
      )}>
      <ArrowLeft className="h-4 w-4" strokeWidth={1.75} aria-hidden />
    </button>
  );
}

type EnvironmentTilesProps = {
  items: ResourceOption[];
  value: string[];
  onSelect: (value: string[]) => void;
  onboardedValues?: string[];
  disabled?: boolean;
  loading?: boolean;
};

const EnvironmentTiles = ({
  items,
  value,
  onSelect,
  onboardedValues = [],
  disabled = false,
  loading = false,
}: EnvironmentTilesProps) => {
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

  useEffect(() => {
    setBlockedMessage(null);
  }, [items, onboardedValues, value]);

  const toggle = (itemValue: string) => {
    if (disabled || loading) return;
    if (onboardedValues.includes(itemValue)) {
      setBlockedMessage(ENVIRONMENT_ALREADY_ONBOARDED_MESSAGE);
      return;
    }
    setBlockedMessage(null);
    onSelect(
      value.includes(itemValue)
        ? value.filter(current => current !== itemValue)
        : [...value, itemValue],
    );
  };

  return (
    <div className="mt-6">
      <p className="mb-2 m-0 flex items-center gap-2 text-[0.9375rem] font-semibold text-[var(--inv-text)]">
        Environment
        {loading ? <Spinner compact /> : null}
      </p>
      <div className="relative flex min-h-10 flex-wrap gap-2">
        {items.length === 0 ? (
          <span className="rounded-[10px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-4 py-2.5 text-xs font-semibold tracking-wide text-[var(--inv-text-muted)]">
            none
          </span>
        ) : (
          items.map(item => {
            const isSelected = value.includes(item.value);
            const isOnboarded = onboardedValues.includes(item.value);
            return (
              <button
                key={item.value}
                type="button"
                disabled={disabled || loading}
                aria-disabled={isOnboarded || undefined}
                onClick={() => toggle(item.value)}
                className={cn(
                  'rounded-[10px] border border-solid px-4 py-2.5 text-xs font-semibold uppercase tracking-wide',
                  isOnboarded
                    ? 'cursor-not-allowed border-[var(--inv-border)] bg-[var(--inv-surface-muted)] text-[var(--inv-text-muted)] opacity-50'
                    : isSelected
                      ? 'border-[var(--inv-accent)] bg-[var(--inv-accent-soft)] text-[var(--inv-accent)]'
                      : 'border-[var(--inv-border)] bg-[var(--inv-surface)] text-[var(--inv-text-muted)]',
                  (disabled || loading) && 'cursor-not-allowed opacity-50',
                )}>
                {item.label}
              </button>
            );
          })
        )}
      </div>
      {blockedMessage ? (
        <p role="alert" className={cn('mb-0 mt-2', fieldErrorClass)}>
          {blockedMessage}
        </p>
      ) : null}
    </div>
  );
};

type CheckOptionCardProps = {
  id: string;
  title: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
};

const CheckOptionCard = ({
  id,
  title,
  description,
  checked,
  onChange,
  disabled = false,
}: CheckOptionCardProps) => (
  <div
    className={cn(
      'mt-6 rounded-[12px] border border-solid px-4 py-3',
      disabled
        ? 'border-[var(--inv-border)] bg-[var(--inv-surface-muted)] opacity-50'
        : checked
          ? 'border-[var(--inv-accent)] bg-[var(--inv-accent-soft)]'
          : 'border-[var(--inv-border)] bg-[var(--inv-surface)]',
    )}>
    <Checkbox
      id={id}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="w-full">
      <span
        className={cn(
          'text-[0.9375rem] font-semibold',
          checked && !disabled
            ? 'text-[var(--inv-accent)]'
            : 'text-[var(--inv-text)]',
        )}>
        {title}
      </span>
    </Checkbox>
    {description ? (
      <p className="mb-0 mt-1 ml-6 text-[0.8125rem] font-normal leading-[1.5] text-[var(--inv-text-secondary)]">
        {description}
      </p>
    ) : null}
  </div>
);

type PrEmailBoxProps = {
  title: string;
  emails: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
};

const PrEmailBox = ({
  title,
  emails,
  onChange,
  disabled = false,
  placeholder = 'Enter reviewer email…',
}: PrEmailBoxProps) => (
  <div
    className={cn(
      'mt-6 rounded-[12px] border border-solid px-4 py-3',
      disabled
        ? 'border-[var(--inv-border)] bg-[var(--inv-surface-muted)] opacity-50'
        : 'border-[var(--inv-border)] bg-[var(--inv-surface)]',
    )}>
    <p className="mb-1 m-0 text-[0.9375rem] font-semibold text-[var(--inv-text)]">
      {title}
    </p>
    <p className="mb-2 m-0 text-[0.8125rem] font-normal leading-[1.5] text-[var(--inv-text-muted)]">
      Add multiple reviewer emails — press Enter after each, or separate with
      commas.
    </p>
    <div className={disabled ? 'pointer-events-none' : undefined}>
      <OnboardingMultiEmailInput
        label=""
        placeholder={placeholder}
        value={emails}
        onChange={onChange}
        collapseAfterFirstLine
      />
    </div>
  </div>
);

type AddRepositoryPickPanelProps = {
  provider: ScmProviderId;
  organization: string;
  organizationAccountId?: string;
  organizationAccountUri?: string;
  pat: string;
  items: ResourceOption[];
  onContinue: (selections: AddRepositoryPickSelections) => void;
  onBack: () => void;
};

const AddRepositoryPickPanel = ({
  provider,
  organization,
  organizationAccountId,
  organizationAccountUri,
  pat,
  items,
  onContinue,
  onBack,
}: AddRepositoryPickPanelProps) => {
  const isGithub = provider === 'github';
  const { data: componentListData } = useComponentList();
  const onboardedComponents = componentListData?.data ?? [];
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedRepo, setSelectedRepo] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedReleases, setSelectedReleases] = useState<string[]>([]);
  const [repos, setRepos] = useState<ResourceOption[]>(isGithub ? items : []);
  const [repoRemoteUrls, setRepoRemoteUrls] = useState<Record<string, string>>(
    {},
  );
  const [adoRepoRecords, setAdoRepoRecords] = useState<
    Record<string, AdoDetectRepository>
  >({});
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [branches, setBranches] = useState<ResourceOption[]>([]);
  const [branchObjectIds, setBranchObjectIds] = useState<Record<string, string>>(
    {},
  );
  const [pipelines, setPipelines] = useState<ResourceOption[]>([]);
  const [stages, setStages] = useState<ResourceOption[]>([]);
  const [releasePipelines, setReleasePipelines] = useState<ResourceOption[]>(
    [],
  );
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>(
    [],
  );
  const [scanOnly, setScanOnly] = useState(false);
  const [frontendUrl, setFrontendUrl] = useState('');
  const [backendUrl, setBackendUrl] = useState('');
  const [applicationPrEmails, setApplicationPrEmails] = useState<string[]>([]);
  const [automationPrEmails, setAutomationPrEmails] = useState<string[]>([]);
  const [captureBeforeAfter, setCaptureBeforeAfter] = useState(false);
  const [captureValidating, setCaptureValidating] = useState(false);
  const [captureUrlError, setCaptureUrlError] = useState<string | null>(null);
  const [captureUrlOk, setCaptureUrlOk] = useState(false);
  const captureValidateTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [workflows, setWorkflows] = useState<ResourceOption[]>([]);
  const [jobs, setJobs] = useState<ResourceOption[]>([]);
  const [environments, setEnvironments] = useState<ResourceOption[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [pipelinesLoading, setPipelinesLoading] = useState(false);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [envLoading, setEnvLoading] = useState(false);

  const resetAzureAfterBranch = useCallback(() => {
    setPipelines([]);
    setSelectedPipeline('');
    setStages([]);
    setSelectedStage('');
    setReleasePipelines([]);
    setSelectedReleases([]);
    setPipelinesLoading(false);
    setStagesLoading(false);
    setReleasesLoading(false);
  }, []);

  const repoRemoteUrl = isGithub
    ? `https://github.com/${organization}/${selectedRepo}`
    : repoRemoteUrls[selectedRepo] ?? '';

  const onboardedEnvironments = useMemo(
    () =>
      getOnboardedEnvironmentsForRepo(
        onboardedComponents,
        organization,
        selectedRepo,
        repoRemoteUrl,
        provider,
      ),
    [
      onboardedComponents,
      organization,
      provider,
      repoRemoteUrl,
      selectedRepo,
    ],
  );

  useEffect(() => {
    if (onboardedEnvironments.length === 0) return;
    setSelectedEnvironments(prev =>
      prev.filter(env => !onboardedEnvironments.includes(env)),
    );
  }, [onboardedEnvironments]);

  const resetGithubAfterBranch = useCallback(() => {
    setWorkflows([]);
    setSelectedWorkflow('');
    setJobs([]);
    setSelectedJob('');
    setEnvironments([]);
    setSelectedEnvironments([]);
    setWorkflowsLoading(false);
    setJobsLoading(false);
    setEnvLoading(false);
  }, []);

  useEffect(
    () => () => {
      if (captureValidateTimer.current) {
        clearTimeout(captureValidateTimer.current);
      }
    },
    [],
  );

  const validateCaptureFrontendUrl = useCallback(
    async (url: string, enabled: boolean) => {
      if (!enabled) {
        setCaptureUrlError(null);
        setCaptureUrlOk(false);
        return true;
      }
      const trimmed = url.trim();
      if (!trimmed) {
        setCaptureUrlError(
          'Unable to access this URL. Please verify the application is running and accessible.',
        );
        setCaptureUrlOk(false);
        return false;
      }
      setCaptureValidating(true);
      try {
        const res = await validateCaptureUrl(trimmed);
        if (!res.ok) {
          setCaptureUrlError(
            res.error ||
              'Unable to access this URL. Please verify the application is running and accessible.',
          );
          setCaptureUrlOk(false);
          return false;
        }
        setCaptureUrlError(null);
        setCaptureUrlOk(true);
        return true;
      } catch {
        setCaptureUrlError(
          'Unable to access this URL. Please verify the application is running and accessible.',
        );
        setCaptureUrlOk(false);
        return false;
      } finally {
        setCaptureValidating(false);
      }
    },
    [],
  );

  const scheduleCaptureUrlValidation = useCallback(
    (url: string, enabled: boolean) => {
      if (captureValidateTimer.current) {
        clearTimeout(captureValidateTimer.current);
        captureValidateTimer.current = null;
      }
      if (!enabled) {
        setCaptureUrlError(null);
        setCaptureUrlOk(false);
        return;
      }
      if (!url.trim()) {
        setCaptureUrlOk(false);
        setCaptureUrlError(null);
        return;
      }
      captureValidateTimer.current = setTimeout(() => {
        void validateCaptureFrontendUrl(url, true);
      }, 500);
    },
    [validateCaptureFrontendUrl],
  );

  const handleCaptureBeforeAfterChange = (checked: boolean) => {
    setCaptureBeforeAfter(checked);
    setCaptureUrlError(null);
    setCaptureUrlOk(false);
    if (checked && frontendUrl.trim()) {
      if (captureValidateTimer.current) {
        clearTimeout(captureValidateTimer.current);
        captureValidateTimer.current = null;
      }
      void validateCaptureFrontendUrl(frontendUrl, true);
    } else if (captureValidateTimer.current) {
      clearTimeout(captureValidateTimer.current);
      captureValidateTimer.current = null;
    }
  };

  useEffect(() => {
    if (isGithub || !selectedProject) {
      if (!isGithub) {
        setRepos([]);
        setAdoRepoRecords({});
        setSelectedProjectId('');
        setSelectedRepo('');
        setBranches([]);
        setSelectedBranch('');
        resetAzureAfterBranch();
      }
      return;
    }

    let cancelled = false;
    setReposLoading(true);
    setRepos([]);
    setAdoRepoRecords({});
    setSelectedProjectId('');
    setSelectedRepo('');
    setBranches([]);
    setSelectedBranch('');
    resetAzureAfterBranch();

    void getRepositoryDetails({
      provider: 'azure-devops',
      body: { organization, project: selectedProject },
      requesterToken: { patToken: pat },
    })
      .then(res => {
        if (cancelled) return;
        const adoRepos = res.data?.value ?? [];
        const projectId = adoRepos[0]?.project?.id ?? '';
        const records = Object.fromEntries(
          adoRepos.map(repo => [
            repo.id,
            {
              id: repo.id,
              name: repo.name,
              url: repo.url,
              remoteUrl: repo.remoteUrl,
              defaultBranch: repo.defaultBranch,
              size: repo.size,
            } satisfies AdoDetectRepository,
          ]),
        );
        setSelectedProjectId(projectId);
        setAdoRepoRecords(records);
        setRepos(
          adoRepos.map(repo => ({
            value: repo.id,
            label: repo.name,
          })),
        );
        setRepoRemoteUrls(
          Object.fromEntries(
            adoRepos.map(repo => [repo.id, repo.remoteUrl ?? '']),
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setReposLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isGithub, organization, pat, selectedProject, resetAzureAfterBranch]);

  useEffect(() => {
    if (!selectedRepo) {
      setBranches([]);
      setBranchObjectIds({});
      setSelectedBranch('');
      if (!isGithub) {
        resetAzureAfterBranch();
      } else {
        resetGithubAfterBranch();
      }
      return;
    }

    let cancelled = false;
    setBranchesLoading(true);
    setBranches([]);
    setBranchObjectIds({});
    setSelectedBranch('');
    if (!isGithub) {
      resetAzureAfterBranch();
    } else {
      resetGithubAfterBranch();
    }

    const load = isGithub
      ? getGithubBranches('github', pat, organization, selectedRepo).then(res =>
          (res.data?.branches ?? []).map(branch => ({
            value: branch.name,
            label: branch.name,
          })),
        )
      : getBranchesDetails(
          'azure-devops',
          {
            organization,
            project: selectedProject,
            repositoryId: selectedRepo,
          },
          { patToken: pat },
        ).then(res => {
          const ids: Record<string, string> = {};
          const options = (res.data?.value ?? []).map(branch => {
            if (branch.objectId) {
              ids[branch.name] = branch.objectId;
            }
            return {
              value: branch.name,
              label: branch.name.replace(/^refs\/heads\//, ''),
            };
          });
          return { options, ids };
        });

    void load
      .then(result => {
        if (cancelled) return;
        if (Array.isArray(result)) {
          setBranches(result);
          setBranchObjectIds({});
        } else {
          setBranches(result.options);
          setBranchObjectIds(result.ids);
        }
      })
      .finally(() => {
        if (!cancelled) setBranchesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isGithub, organization, pat, selectedProject, selectedRepo, resetAzureAfterBranch, resetGithubAfterBranch]);

  useEffect(() => {
    if (isGithub || !selectedBranch) {
      if (!isGithub) resetAzureAfterBranch();
      return;
    }

    let cancelled = false;
    setPipelinesLoading(true);
    setPipelines([]);
    setSelectedPipeline('');
    setStages([]);
    setSelectedStage('');
    setReleasePipelines([]);
    setSelectedReleases([]);

    void getPipelineDetails({
      provider: 'azure-devops',
      body: { organization, project: selectedProject },
      requesterToken: { patToken: pat },
    })
      .then(data => {
        if (cancelled) return;
        setPipelines(
          (data ?? []).map(pipeline => ({
            value: String(pipeline.id),
            label: pipeline.name,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setPipelinesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isGithub, organization, pat, selectedProject, selectedBranch, resetAzureAfterBranch]);

  useEffect(() => {
    if (isGithub || !selectedPipeline) {
      if (!isGithub) {
        setStages([]);
        setSelectedStage('');
        setReleasePipelines([]);
        setSelectedReleases([]);
        setStagesLoading(false);
        setReleasesLoading(false);
      }
      return;
    }

    let cancelled = false;
    setStagesLoading(true);
    setReleasesLoading(true);
    setStages([]);
    setSelectedStage('');
    setReleasePipelines([]);
    setSelectedReleases([]);

    void getPipelineStages({
      provider: 'azure-devops',
      body: {
        organization,
        project: selectedProject,
        pipelineId: selectedPipeline,
        orgUrl: '',
      },
      requesterToken: { patToken: pat },
    })
      .then(res => {
        if (cancelled) return;
        setStages(
          (res.data?.stages ?? []).map(stage => ({
            value: stage.name,
            label: stage.name,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setStagesLoading(false);
      });

    void getReleasePipelines({
      provider: 'azure-devops',
      body: {
        organization,
        project: selectedProject,
        orgUrl: '',
      },
      requesterToken: { patToken: pat },
    })
      .then(res => {
        if (cancelled) return;
        setReleasePipelines(
          (res.data?.pipelines ?? []).map(pipeline => ({
            value: String(pipeline.id),
            label: pipeline.name,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setReleasesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isGithub, organization, pat, selectedProject, selectedPipeline]);

  useEffect(() => {
    if (!isGithub || !selectedBranch || !selectedRepo) {
      if (isGithub) resetGithubAfterBranch();
      return;
    }

    let cancelled = false;
    setWorkflowsLoading(true);
    setEnvLoading(true);
    setWorkflows([]);
    setSelectedWorkflow('');
    setJobs([]);
    setSelectedJob('');
    setEnvironments([]);
    setSelectedEnvironments([]);

    void getGithubWorkflows('github', pat, organization, selectedRepo)
      .then(res => {
        if (cancelled) return;
        setWorkflows(
          (res.data?.workflows ?? []).map(workflow => ({
            value: String(workflow.id),
            label: workflow.name,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setWorkflowsLoading(false);
      });

    void getGithubEnvironments('github', pat, organization, selectedRepo)
      .then(res => {
        if (cancelled) return;
        setEnvironments(
          (res.data?.environments ?? []).map(environment => ({
            value: environment.name,
            label: environment.name,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setEnvLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isGithub,
    organization,
    pat,
    selectedRepo,
    selectedBranch,
    resetGithubAfterBranch,
  ]);

  useEffect(() => {
    if (!isGithub || !selectedWorkflow || !selectedBranch || !selectedRepo) {
      if (isGithub) {
        setJobs([]);
        setSelectedJob('');
        setJobsLoading(false);
      }
      return;
    }

    let cancelled = false;
    setJobsLoading(true);
    setJobs([]);
    setSelectedJob('');

    void getGithubWorkflowJobs(
      'github',
      pat,
      organization,
      selectedRepo,
      selectedWorkflow,
      selectedBranch,
    )
      .then(res => {
        if (cancelled) return;
        setJobs(
          (res.data?.jobs ?? []).map(job => ({
            value: job.name,
            label: job.name,
          })),
        );
      })
      .finally(() => {
        if (!cancelled) setJobsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isGithub,
    organization,
    pat,
    selectedRepo,
    selectedBranch,
    selectedWorkflow,
  ]);

  const buildSelections = (): AddRepositoryPickSelections => {
    const releasePipelineLabels: Record<string, string> = {};
    for (const pipeline of releasePipelines) {
      releasePipelineLabels[pipeline.value] = pipeline.label;
    }
    return {
      provider,
      organization,
      organizationAccountId,
      organizationAccountUri,
      pat,
      selectedProject,
      selectedProjectId: isGithub ? undefined : selectedProjectId,
      selectedRepo,
      selectedRepoName: isGithub
        ? selectedRepo
        : repos.find(repo => repo.value === selectedRepo)?.label ?? '',
      repoRemoteUrl: isGithub
        ? `https://github.com/${organization}/${selectedRepo}`
        : repoRemoteUrls[selectedRepo] ?? '',
      selectedRepoDetectData: isGithub
        ? undefined
        : adoRepoRecords[selectedRepo],
      selectedBranch,
      selectedBranchObjectId: isGithub
        ? undefined
        : branchObjectIds[selectedBranch],
      selectedPipeline,
      selectedPipelineName:
        pipelines.find(pipeline => pipeline.value === selectedPipeline)
          ?.label ?? '',
      selectedStage,
      selectedReleases,
      releasePipelineLabels,
      selectedWorkflow,
      selectedWorkflowName:
        workflows.find(workflow => workflow.value === selectedWorkflow)
          ?.label ?? '',
      selectedJob,
      selectedEnvironments,
      scanOnly,
      frontendUrl,
      backendUrl,
      applicationPrEmails,
      automationPrEmails,
      captureBeforeAfter,
    };
  };

  const handleContinue = () => {
    if (!selectedRepo || !selectedBranch) return;
    onContinue(buildSelections());
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--inv-bg)]">
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-8 pt-8 max-[520px]:px-5">
        <AddRepositoryBackButton onBack={onBack} />
        <OnboardingPageHeader
          eyebrow="ONBOARDING"
          title="Add a Repository"
        />

        {isGithub ? (
          <FilterSelect
            id="add-repository-repo"
            label="Pick your Repository"
            items={items}
            value={selectedRepo}
            onSelect={setSelectedRepo}
            className="mt-0"
          />
        ) : (
          <>
            <FilterSelect
              id="add-repository-project"
              label="Find your project"
              items={items}
              value={selectedProject}
              onSelect={setSelectedProject}
              className="mt-0"
            />
            <FilterSelect
              id="add-repository-ado-repo"
              label="Pick your Repository"
              items={repos}
              value={selectedRepo}
              onSelect={setSelectedRepo}
              disabled={!selectedProject || reposLoading}
              loading={reposLoading}
            />
          </>
        )}

        <FilterSelect
          id="add-repository-branch"
          label="Choose your branch"
          items={branches}
          value={selectedBranch}
          onSelect={setSelectedBranch}
          disabled={!selectedRepo || branchesLoading}
          loading={branchesLoading}
        />

        {isGithub ? (
          <>
            <FilterSelect
              id="add-repository-workflow"
              label="Workflow"
              items={workflows}
              value={selectedWorkflow}
              onSelect={setSelectedWorkflow}
              disabled={!selectedBranch || workflowsLoading}
              loading={workflowsLoading}
            />
            <FilterSelect
              id="add-repository-workflow-jobs"
              label="Workflow Jobs"
              items={jobs}
              value={selectedJob}
              onSelect={setSelectedJob}
              disabled={!selectedWorkflow || jobsLoading}
              loading={jobsLoading}
            />
            <EnvironmentTiles
              items={environments}
              value={selectedEnvironments}
              onSelect={setSelectedEnvironments}
              onboardedValues={onboardedEnvironments}
              disabled={!selectedBranch || envLoading}
              loading={envLoading}
            />
          </>
        ) : (
          <>
            <FilterSelect
              id="add-repository-pipeline"
              label="Choose your Pipeline"
              items={pipelines}
              value={selectedPipeline}
              onSelect={setSelectedPipeline}
              disabled={!selectedBranch || pipelinesLoading}
              loading={pipelinesLoading}
            />
            <FilterSelect
              id="add-repository-pipeline-stages"
              label="Pipeline Stages"
              items={stages}
              value={selectedStage}
              onSelect={setSelectedStage}
              disabled={!selectedPipeline || stagesLoading}
              loading={stagesLoading}
            />
            <FilterSelect
              id="add-repository-release-pipelines"
              label="Release Pipelines"
              items={releasePipelines}
              value={selectedReleases}
              onSelect={setSelectedReleases}
              multiple
              disabled={!selectedPipeline || releasesLoading}
              loading={releasesLoading}
            />
          </>
        )}

        <CheckOptionCard
          id="add-repository-scan-only"
          title="Scan only — do not raise pull requests"
          description="Everything here is saved and the repository is added. Nothing is written to it and no instrumentation is applied. Raise the pull requests whenever you are ready."
          checked={scanOnly}
          onChange={checked => {
            setScanOnly(checked);
            if (checked) setCaptureBeforeAfter(false);
          }}
        />

        <PrEmailBox
          title="Application PR"
          emails={applicationPrEmails}
          onChange={setApplicationPrEmails}
          disabled={scanOnly}
        />

        <PrEmailBox
          title="Automation PR"
          emails={automationPrEmails}
          onChange={setAutomationPrEmails}
          disabled={scanOnly}
        />

        <div className="mt-6">
          <p className="mb-1 m-0 text-[0.9375rem] font-semibold text-[var(--inv-text)]">
            Frontend URL
          </p>
          <p className="mb-2 m-0 text-[0.8125rem] font-normal leading-[1.5] text-[var(--inv-text-muted)]">
            Used for the baseline screenshot before instrumentation, and to
            open manual testing sessions.
          </p>
          <Input
            id="add-repository-frontend-url"
            type="url"
            value={frontendUrl}
            onChange={event => {
              const next = event.target.value;
              setFrontendUrl(next);
              setCaptureUrlOk(false);
              if (captureBeforeAfter) {
                scheduleCaptureUrlValidation(next, true);
              } else {
                setCaptureUrlError(null);
              }
            }}
            onBlur={() => {
              if (captureBeforeAfter && frontendUrl.trim()) {
                if (captureValidateTimer.current) {
                  clearTimeout(captureValidateTimer.current);
                  captureValidateTimer.current = null;
                }
                void validateCaptureFrontendUrl(frontendUrl, true);
              }
            }}
            placeholder="https://"
            className="font-mono shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]"
          />
        </div>

        <div className="mt-6">
          <p className="mb-1 m-0 text-[0.9375rem] font-semibold text-[var(--inv-text)]">
            Backend URL
          </p>
          <p className="mb-2 m-0 text-[0.8125rem] font-normal leading-[1.5] text-[var(--inv-text-muted)]">
            Used when coverage is pulled from a running backend service.
          </p>
          <Input
            id="add-repository-backend-url"
            type="url"
            value={backendUrl}
            onChange={event => setBackendUrl(event.target.value)}
            placeholder="https://"
            className="font-mono shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]"
          />
        </div>

        <CheckOptionCard
          id="add-repository-capture-before-after"
          title="Capture before and after instrumentation"
          checked={captureBeforeAfter}
          disabled={scanOnly}
          onChange={handleCaptureBeforeAfterChange}
        />
        {captureBeforeAfter && !scanOnly ? (
          <div className="mt-2 ml-1">
            {captureValidating ? (
              <p className="m-0 text-[0.8125rem] text-[var(--inv-text-muted)]">
                Validating URL…
              </p>
            ) : null}
            {captureUrlOk ? (
              <p className="m-0 text-[0.8125rem] text-emerald-600">
                URL verified
              </p>
            ) : null}
            {captureUrlError ? (
              <p role="alert" className={cn('m-0', fieldErrorClass)}>
                {captureUrlError}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="shrink-0 border-t border-[var(--inv-border)] bg-[var(--inv-bg)] px-8 py-4 max-[520px]:px-5">
        <div className="flex items-center justify-between gap-4">
          <p className="m-0 text-[0.8125rem] leading-[1.5] text-[var(--inv-text-muted)]">
            {scanOnly
              ? 'Scans both repositories · nothing is written'
              : 'Scans, instruments, then raises 2 pull requests'}
          </p>
          <Button
            type="button"
            className="h-10 shrink-0 whitespace-nowrap"
            disabled={!selectedRepo || !selectedBranch}
            onClick={handleContinue}>
            {scanOnly ? 'Scan only' : 'Scan and instrument'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddRepositoryPickPanel;
